import { LANDMARKS, NPCS, landmarkById, npcById, type LandmarkId, type NpcId } from "./catalog";
import { setMuted, type SaveState } from "./save";

function must<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) {
    throw new Error(`missing #${id}`);
  }
  return el as T;
}

type CollectItem =
  | { kind: "postcard"; id: LandmarkId }
  | { kind: "badge"; id: NpcId };

export class GameUI {
  readonly hud = must<HTMLElement>("hud");
  readonly gate = must<HTMLElement>("gate");
  readonly enter = must<HTMLButtonElement>("btn-enter");
  readonly collectBtn = must<HTMLButtonElement>("btn-collect");
  readonly muteBtn = must<HTMLButtonElement>("btn-mute");
  readonly interactBtn = must<HTMLButtonElement>("btn-interact");
  readonly hint = must<HTMLParagraphElement>("hint");
  readonly drawer = must<HTMLElement>("drawer");
  readonly drawerClose = must<HTMLButtonElement>("drawer-close");
  readonly postcardList = must<HTMLUListElement>("postcard-list");
  readonly badgeList = must<HTMLUListElement>("badge-list");
  readonly progress = must<HTMLParagraphElement>("progress-summary");
  readonly talkDialog = must<HTMLDialogElement>("talk-dialog");
  readonly talkName = must<HTMLHeadingElement>("talk-name");
  readonly talkLine = must<HTMLParagraphElement>("talk-line");
  readonly talkKicker = must<HTMLParagraphElement>("talk-kicker");
  readonly talkNext = must<HTMLButtonElement>("talk-next");
  readonly talkGuide = must<HTMLButtonElement>("talk-guide");
  readonly collectDialog = must<HTMLDialogElement>("collect-dialog");
  readonly collectKicker = must<HTMLParagraphElement>("collect-kicker");
  readonly collectTitle = must<HTMLHeadingElement>("collect-title");
  readonly collectBody = must<HTMLParagraphElement>("collect-body");
  readonly collectArt = must<HTMLElement>("collect-art");

  private save: SaveState;
  private queue: CollectItem[] = [];
  private talkNpc: NpcId | null = null;
  private talkIndex = 0;
  private lastFocus: HTMLElement | null = null;

  onEnter: () => void = () => {};
  onGuide: (id: NpcId) => void = () => {};
  onTalk: (id: NpcId) => void = () => {};
  onMute: (muted: boolean) => void = () => {};

  constructor(save: SaveState) {
    this.save = save;
    this.enter.addEventListener("click", () => this.onEnter());
    this.collectBtn.addEventListener("click", () => this.toggleDrawer());
    this.drawerClose.addEventListener("click", () => this.closeDrawer());
    this.muteBtn.addEventListener("click", () => this.toggleMute());
    this.interactBtn.addEventListener("click", () => {
      if (this.talkNpc) {
        this.onTalk(this.talkNpc);
      }
    });
    this.talkNext.addEventListener("click", () => this.advanceTalk());
    this.talkGuide.addEventListener("click", () => {
      if (!this.talkNpc) {
        return;
      }
      const id = this.talkNpc;
      this.talkDialog.close();
      this.onGuide(id);
    });
    this.talkDialog.addEventListener("close", () => {
      this.talkNpc = null;
    });
    this.collectDialog.addEventListener("close", () => this.flushQueue());
    this.renderMute();
    this.renderProgress();
    this.renderDrawer();
    this.enter.focus();
  }

  get blocking(): boolean {
    return !this.gate.hidden || this.talkDialog.open || this.collectDialog.open;
  }

  startPlay(): void {
    this.gate.hidden = true;
    this.hud.hidden = false;
  }

  setHint(text: string | null, interact: NpcId | null): void {
    this.talkNpc = interact;
    if (!text) {
      this.hint.hidden = true;
      this.interactBtn.hidden = true;
      return;
    }
    this.hint.hidden = false;
    this.hint.textContent = text;
    this.interactBtn.hidden = !interact;
  }

  openTalk(id: NpcId): void {
    const npc = npcById(id);
    this.talkNpc = id;
    this.talkIndex = 0;
    this.talkKicker.textContent = npc.title;
    this.talkName.textContent = npc.name;
    this.talkLine.textContent = npc.lines[0] ?? "";
    this.talkNext.hidden = npc.lines.length < 2;
    this.talkDialog.showModal();
    this.talkNext.hidden ? this.talkGuide.focus() : this.talkNext.focus();
  }

  enqueueCollect(item: CollectItem): void {
    if (this.collectDialog.open || this.talkDialog.open) {
      this.queue.push(item);
      return;
    }
    this.showCollect(item);
  }

  showCollect(item: CollectItem): void {
    if (item.kind === "postcard") {
      const def = landmarkById(item.id);
      this.collectKicker.textContent = "明信片";
      this.collectTitle.textContent = def.postcardTitle;
      this.collectBody.textContent = def.postcardBody;
      this.collectArt.innerHTML = postcardArt(item.id);
    } else {
      const def = npcById(item.id);
      this.collectKicker.textContent = "徽章";
      this.collectTitle.textContent = def.badgeTitle;
      this.collectBody.textContent = def.badgeBody;
      this.collectArt.innerHTML = `<div class="art-badge ${item.id}"><span class="seal"></span></div>`;
    }
    this.collectDialog.showModal();
  }

  openFromDrawer(item: CollectItem): void {
    this.closeDrawer();
    this.showCollect(item);
  }

  refresh(save: SaveState): void {
    this.save = save;
    this.renderProgress();
    this.renderDrawer();
    this.renderMute();
  }

  toggleMute(): void {
    setMuted(this.save, !this.save.muted);
    this.renderMute();
    this.onMute(this.save.muted);
  }

  get muted(): boolean {
    return this.save.muted;
  }

  toggleDrawer(): void {
    if (this.drawer.hidden) {
      this.lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.drawer.hidden = false;
      this.renderDrawer();
      this.drawerClose.focus();
    } else {
      this.closeDrawer();
    }
  }

  closeDrawer(): void {
    this.drawer.hidden = true;
    this.lastFocus?.focus();
  }

  closeModals(): void {
    if (this.talkDialog.open) {
      this.talkDialog.close();
    }
    if (this.collectDialog.open) {
      this.collectDialog.close();
    }
    if (!this.drawer.hidden) {
      this.closeDrawer();
    }
  }

  private advanceTalk(): void {
    if (!this.talkNpc) {
      return;
    }
    const lines = npcById(this.talkNpc).lines;
    this.talkIndex = Math.min(this.talkIndex + 1, lines.length - 1);
    this.talkLine.textContent = lines[this.talkIndex] ?? "";
    if (this.talkIndex >= lines.length - 1) {
      this.talkNext.hidden = true;
      this.talkGuide.focus();
    }
  }

  private flushQueue(): void {
    const next = this.queue.shift();
    if (next) {
      window.setTimeout(() => this.showCollect(next), 120);
    }
  }

  private renderMute(): void {
    this.muteBtn.setAttribute("aria-pressed", this.save.muted ? "true" : "false");
    this.muteBtn.textContent = this.save.muted ? "声音关" : "声音开";
    this.muteBtn.setAttribute("aria-label", this.save.muted ? "打开声音" : "静音");
  }

  private renderProgress(): void {
    this.progress.textContent = `明信片 ${this.save.postcards.length}/3 · 徽章 ${this.save.badges.length}/8`;
  }

  private renderDrawer(): void {
    this.postcardList.replaceChildren();
    for (const item of LANDMARKS) {
      const unlocked = this.save.postcards.includes(item.id);
      this.postcardList.append(this.row(unlocked, item.name, "地标记录", () => this.openFromDrawer({ kind: "postcard", id: item.id })));
    }
    this.badgeList.replaceChildren();
    for (const item of NPCS) {
      const unlocked = this.save.badges.includes(item.id);
      this.badgeList.append(this.row(unlocked, item.name, item.badgeTitle, () => this.openFromDrawer({ kind: "badge", id: item.id })));
    }
  }

  private row(unlocked: boolean, name: string, meta: string, onOpen: () => void): HTMLLIElement {
    const li = document.createElement("li");
    if (!unlocked) {
      const box = document.createElement("div");
      box.className = "locked";
      box.innerHTML = `<span class="name">未记录</span><span class="meta">靠近后解锁</span>`;
      li.append(box);
      return li;
    }
    const btn = document.createElement("button");
    btn.type = "button";
    btn.innerHTML = `<span class="name">${name}</span><span class="meta">${meta}</span>`;
    btn.addEventListener("click", onOpen);
    li.append(btn);
    return li;
  }
}

function postcardArt(id: LandmarkId): string {
  if (id === "workshop") {
    return `<div class="art-workshop"><span class="hall"></span><span class="roof"></span><span class="stack"></span></div>`;
  }
  if (id === "lighthouse") {
    return `<div class="art-lighthouse"><span class="bar-up"></span><span class="bar-down"></span><span class="tower"></span><span class="lamp"></span></div>`;
  }
  return `<div class="art-garden"><span class="stem"></span><span class="stem"></span><span class="stem"></span><span class="orb"></span><span class="orb"></span><span class="orb"></span></div>`;
}
